/**
 * ParticleBackground — shared full-bleed particle visual (ported from the Flow
 * editor's FlowBackgroundVisual so every editor can use it). A 2D canvas that
 * paints abstract motion reacting to either the NOTES (energy pulses near the
 * playhead) or the MUSIC (live analyser amplitude). Styles: aurora / embers /
 * ripple, tinted with the accent theme. Style + source come from the shared
 * themeStore; the reactivity sources are passed in per-editor.
 */

import React, { useEffect, useRef } from 'react';
import { useTheme } from '../components/ui/themeStore';
import { accentRgb } from '../components/ui/themeColors';
import { useVizStore } from './vizStore';

type RGB = { r: number; g: number; b: number };
interface Particle { x: number; y: number; vx: number; vy: number; life: number; max: number; size: number; col: RGB; }
interface Ring { x: number; y: number; r: number; maxR: number; life: number; max: number; col: RGB; w: number; }

interface Props {
  /** Live analyser tapping the playing song (drives the "music" source). */
  getAnalyser?: () => AnalyserNode | null;
  /** 0..1 energy from chart notes near the playhead (drives the "notes"/"map" source). */
  getNoteEnergy?: () => number;
}

export default function ParticleBackground({ getAnalyser, getNoteEnergy }: Props) {
  const bgVisual = useTheme(s => s.bgVisual);
  // Reactivity source follows the shared panel's "React to": audio → music
  // amplitude, notes → note-energy pulses.
  const reactTo  = useVizStore(s => s.reactTo);
  const source: 'music' | 'map' = reactTo === 'audio' ? 'music' : 'map';
  const themeAccent = useTheme(s => s.accent);
  const themeAccentHex = useTheme(s => s.accentHex);
  const getAnalyserRef = useRef(getAnalyser);
  const getNoteEnergyRef = useRef(getNoteEnergy);
  getAnalyserRef.current = getAnalyser;
  getNoteEnergyRef.current = getNoteEnergy;

  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    if (bgVisual === 'none') return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const accent = accentRgb(0.72, 0.2);
    const particles: Particle[] = [];
    const rings: Ring[] = [];
    const MAX_P = 360;

    let W = 1, H = 1; const dpr = Math.min(2, window.devicePixelRatio || 1);
    const resize = () => {
      const r = canvas.getBoundingClientRect();
      W = Math.max(1, r.width); H = Math.max(1, r.height);
      canvas.width = Math.round(W * dpr); canvas.height = Math.round(H * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    resize();
    const ro = new ResizeObserver(resize);
    ro.observe(canvas);

    const spawnBurst = (xFrac: number, col: RGB, strong: boolean) => {
      const x = xFrac * W, y = H * 0.86;
      const n = bgVisual === 'embers' ? (strong ? 22 : 13) : (strong ? 10 : 6);
      if (bgVisual === 'aurora') {
        const blobs = strong ? 2 : 1;
        for (let i = 0; i < blobs && particles.length < MAX_P; i++) {
          particles.push({
            x: (0.1 + Math.random() * 0.8) * W, y: (0.15 + Math.random() * 0.65) * H,
            vx: (Math.random() - 0.5) * 14, vy: (Math.random() - 0.5) * 14,
            life: 0, max: 0.22 + Math.random() * 0.25,
            size: 28 + Math.pow(Math.random(), 1.6) * 210, col,
          });
        }
        return;
      }
      if (bgVisual === 'ripple') {
        rings.push({ x, y, r: 6, maxR: H * (strong ? 0.85 : 0.55), life: 0, max: strong ? 1.5 : 1.1, col, w: strong ? 3 : 2 });
        if (rings.length > 48) rings.shift();
        return;
      }
      const dir = xFrac - 0.5;
      const boost = strong ? 1.3 : 1;
      for (let i = 0; i < n && particles.length < MAX_P; i++) {
        particles.push({
          x: (0.5 + dir * 0.12 + (Math.random() - 0.5) * 0.06) * W, y: (0.04 + Math.random() * 0.14) * H,
          vx: (dir * 180 + (Math.random() - 0.5) * 90) * boost, vy: (150 + Math.random() * 150) * boost,
          life: 0, max: 1.0 + Math.random() * 1.1, size: 2 + Math.random() * 3.2, col,
        });
      }
    };

    let raf = 0; let last = performance.now();
    let prevLevel = 0; let ambientAcc = 0; let prevNoteE = 0;
    let freq: Uint8Array | null = null;

    const frame = (now: number) => {
      const dt = Math.min(0.05, (now - last) / 1000); last = now;

      if (source === 'music') {
        // Live amplitude from the analyser (low-mid energy).
        const an = getAnalyserRef.current?.();
        let level = 0;
        if (an) {
          if (!freq || freq.length !== an.frequencyBinCount) freq = new Uint8Array(an.frequencyBinCount);
          an.getByteFrequencyData(freq);
          let sum = 0; const lo = 2, hi = Math.min(freq.length, 220);
          for (let b = lo; b < hi; b++) sum += freq[b];
          level = sum / ((hi - lo) * 255);
        }
        if (level - prevLevel > 0.16 && level > 0.28) spawnBurst(0.18 + Math.random() * 0.64, accent, level > 0.6);
        ambientAcc += level * dt * (bgVisual === 'embers' ? 26 : 8);
        while (ambientAcc >= 1) { ambientAcc -= 1; if (particles.length < MAX_P) spawnBurst(Math.random(), accent, false); }
        prevLevel = level;
      } else {
        // Notes: pulse on the rising edge of note energy near the playhead.
        const e = Math.max(0, Math.min(1, getNoteEnergyRef.current?.() ?? 0));
        if (e - prevNoteE > 0.25 && e > 0.5) spawnBurst(0.18 + Math.random() * 0.64, accent, e > 0.8);
        prevNoteE = e;
      }

      ctx.globalCompositeOperation = 'source-over';
      ctx.fillStyle = `rgba(0,0,0,${bgVisual === 'aurora' ? 0.16 : 0.18})`;
      ctx.fillRect(0, 0, W, H);
      ctx.globalCompositeOperation = 'lighter';

      for (let i = particles.length - 1; i >= 0; i--) {
        const p = particles[i];
        p.life += dt;
        if (p.life >= p.max) { particles.splice(i, 1); continue; }
        p.x += p.vx * dt; p.y += p.vy * dt;
        p.vy += (bgVisual === 'aurora' ? 0 : 30) * dt;
        p.vx *= 0.99;
        let a: number, rad: number;
        if (bgVisual === 'aurora') {
          const env = p.life / p.max;
          a = 0.05 * Math.sin(env * Math.PI);
          rad = p.size * (0.85 + env * 0.35);
        } else {
          const t = 1 - p.life / p.max;
          a = 0.5 * t;
          rad = p.size * (0.6 + (p.life / p.max) * 1.4);
        }
        const g = ctx.createRadialGradient(p.x, p.y, 0, p.x, p.y, rad);
        g.addColorStop(0, `rgba(${p.col.r},${p.col.g},${p.col.b},${a})`);
        g.addColorStop(1, `rgba(${p.col.r},${p.col.g},${p.col.b},0)`);
        ctx.fillStyle = g;
        ctx.beginPath(); ctx.arc(p.x, p.y, rad, 0, Math.PI * 2); ctx.fill();
      }

      for (let i = rings.length - 1; i >= 0; i--) {
        const rg = rings[i];
        rg.life += dt;
        if (rg.life >= rg.max) { rings.splice(i, 1); continue; }
        const t = rg.life / rg.max;
        rg.r = 6 + (rg.maxR - 6) * t;
        ctx.strokeStyle = `rgba(${rg.col.r},${rg.col.g},${rg.col.b},${0.6 * (1 - t)})`;
        ctx.lineWidth = rg.w;
        ctx.beginPath(); ctx.arc(rg.x, rg.y, rg.r, 0, Math.PI * 2); ctx.stroke();
      }

      raf = requestAnimationFrame(frame);
    };
    raf = requestAnimationFrame(frame);

    return () => { cancelAnimationFrame(raf); ro.disconnect(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bgVisual, source, themeAccent, themeAccentHex]);

  if (bgVisual === 'none') return null;

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: 'absolute', inset: 0, width: '100%', height: '100%',
        pointerEvents: 'none', mixBlendMode: 'screen', zIndex: 1, opacity: 0.9,
      }}
    />
  );
}
